export type ObservableAction<T> = (data: T) => void;
export type ObservableSetter<T> = T | ((prevData: T) => T);

export class Observable<T> {
    private _data: T;
    private _subscriptions: Map<number, ObservableAction<T>>;

    private static id = 0;

    constructor(initialData: T) {
        this._data = initialData;

        this._subscriptions = new Map();
    }

    get data() {
        return this._data;
    }

    /**
     * subscribe to the observable
     *
     * the subscription action is defined with a callback function which is called on every broadcast
     * @param action
     * @returns
     */
    subscribe(action: ObservableAction<T>, immediate = false) {
        const key = Observable.id++;
        this._subscriptions.set(key, action);

        if (immediate) {
            action(this.data);
        }

        const resubscribe = () => {
            this.subscribe(action);
        };

        const unsubscribe = () => {
            this._unsubscribeAtKey(key);
            return resubscribe;
        };

        return unsubscribe;
    }

    unsubscribeAll() {
        for (const key of this._subscriptions.keys()) {
            this._unsubscribeAtKey(key);
        }
    }

    /**
     * updates the observable data without broadcasting it
     * @param data
     * @returns
     */
    set(data: ObservableSetter<T>) {
        if (data instanceof Function) {
            this._data = data(this._data);
        } else {
            this._data = data;
        }
        return this;
    }

    /**
     * broadcasts the observable data to all subscribers
     *
     * is new data provided then it will be updated first and then broadcasted after
     * @returns
     */
    broadcast(data: ObservableSetter<T>) {
        this.set(data);
        this._updateSubscriptions();
        return this;
    }

    private _updateSubscriptions() {
        for (const subscription of this._subscriptions) {
            subscription[1](this._data);
        }
    }

    private _unsubscribeAtKey(key: number) {
        this._subscriptions.delete(key);
    }
}

export function combine<T extends unknown[]>(
    ...observables: { [K in keyof T]: Observable<T[K]> }
): Observable<T> {
    const combined = new Observable<T>(
        observables.map((o) => o.data) as unknown as T,
    );

    observables.forEach((obs, index) => {
        obs.subscribe((v) => {
            combined.broadcast((prev) => {
                const copy = [...prev] as unknown as T;
                copy[index] = v;
                return copy;
            });
        });
    });

    return combined;
}
